// Accepting an invitation.
//
// The token in the link is the only thing that proves anything; the email on the
// invitation is a hint about who it was for. Acceptance binds the workspace to
// whoever is signed in when they click — a client-supplied address is never
// authentication (ADR-0011).

import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { acceptInvitation } from "../lib/people";
import { useWorkspace } from "../lib/WorkspaceContext";
import { logError } from "../lib/logger";

export function Join() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh, select } = useWorkspace();
  const [state, setState] = useState<"working" | "done" | "error">("working");
  const [message, setMessage] = useState<string | null>(null);
  const [until, setUntil] = useState<string | null>(null);

  const token = params.get("token");

  useEffect(() => {
    if (!token) {
      setState("error");
      setMessage("This link is missing its invitation code.");
      return;
    }
    acceptInvitation(token)
      .then(async (result) => {
        setState("done");
        setMessage(`You have joined ${result.workspace_name} as ${result.role}.`);
        setUntil(result.access_expires_at);
        await refresh();
        select(result.workspace_id);
        // Land them somewhere useful rather than on a dead confirmation page.
        // /start picks the screen from what this workspace has already done
        // and what this role may do in it (ADR-0016).
        setTimeout(() => navigate("/start"), 1200);
      })
      .catch((e) => {
        setState("error");
        setMessage(e.message ?? String(e));
        void logError("auth", e, { step: "accept_invitation" });
      });
  }, [token, refresh, select, navigate]);

  return (
    <div className="card">
      <h1>Invitation</h1>
      {state === "working" && <p className="muted">Checking your invitation…</p>}
      {state === "done" && (
        <>
          <p className="ok-text">{message}</p>
          {/* A fixed term is only a control if the person working under it knows. */}
          {until && (
            <p className="muted">
              This access ends on{" "}
              {new Date(until).toLocaleDateString(undefined, {
                day: "numeric", month: "long", year: "numeric",
              })}
              . You will need a new invitation to continue after that.
            </p>
          )}
        </>
      )}
      {state === "error" && (
        <>
          <p className="err">{message}</p>
          <p className="muted">
            Ask whoever invited you for a new link, or <Link to="/compose">go to the app</Link>.
          </p>
        </>
      )}
    </div>
  );
}
